import type { Edge, Node } from '@xyflow/react';
import { TipoAlvo, type AlvoDto, type ConectorDto, type NotaDto } from '@nexital/shared';
import { posicaoAbsoluta, type Ponto } from '../../domain/notas';

export type DadosNota = { nota: NotaDto };
export type NoNotaTipo = Node<DadosNota, 'nota'>;
export type DadosSeta = { rotulo: string | null; setaUuid: string };
export type ArestaSetaTipo = Edge<DadosSeta, 'seta'>;

const LARGURA_PADRAO = 200;
const ALTURA_PADRAO = 160;

/** Id do nó no React Flow: c: cartão (processo), g: grupo, n: nota. */
export function idDoAlvo(alvo: AlvoDto): string {
  if (alvo.tipo === TipoAlvo.Processo) return `c:${alvo.uuid}`;
  if (alvo.tipo === TipoAlvo.Grupo) return `g:${alvo.uuid}`;
  return `n:${alvo.uuid}`;
}

/** A seta selecionada continua selecionada quando as setas são relidas (o Delete precisa dela). */
export function manterSelecao<E extends Edge>(novas: E[]) {
  return (atuais: E[]) => {
    const selecionadas = new Set(atuais.filter((a) => a.selected).map((a) => a.id));
    return novas.map((a) => (selecionadas.has(a.id) ? { ...a, selected: true } : a));
  };
}

/**
 * Notas viram nós (posição absoluta; presas acompanham o alvo, RQ-NOT-003) e setas viram arestas que saem da origem
 * (primeiro clique) e apontam para o destino (segundo clique), RQ-NOT-004. Toda nota visível se arrasta (RQ-NOT-010),
 * menos a acompanhada, que o superior só lê.
 * `posicaoDoAlvo` devolve a posição absoluta do cartão ou grupo no quadro (null se não está visível).
 */
export function montarNosNotas(
  notas: NotaDto[],
  setas: ConectorDto[],
  posicaoDoAlvo: (alvo: AlvoDto) => Ponto | null,
): { nos: NoNotaTipo[]; arestas: ArestaSetaTipo[] } {
  const nos: NoNotaTipo[] = notas.map((nota) => ({
    id: `n:${nota.uuid}`,
    type: 'nota',
    position: posicaoAbsoluta(nota, posicaoDoAlvo),
    width: nota.largura ?? LARGURA_PADRAO,
    height: nota.altura ?? ALTURA_PADRAO,
    draggable: !nota.acompanhada,
    zIndex: 10,
    data: { nota },
  }));
  const arestas: ArestaSetaTipo[] = setas.map((seta) => ({
    id: `s:${seta.uuid}`,
    type: 'seta',
    source: idDoAlvo(seta.origem),
    target: idDoAlvo(seta.destino),
    data: { rotulo: seta.rotulo, setaUuid: seta.uuid },
  }));
  return { nos, arestas };
}
